<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Materiais</h5>
      <router-link
        v-if="hasPermission('adicionar_materiais')"
        to="/ajuda/materiais/novo"
      >
        <b-button variant="primary" class="d-flex align-items-center">
          <i class="bi bi-file-earmark-plus-fill mr-2" title="Novo Material"></i>
        </b-button>
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.titulo"
        placeholder="Filtrar por título"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.descricao"
        placeholder="Filtrar por descrição"
        class="form-control"
        @input="filtrar"
      />

      <!-- ✅ Público: select -->
      <b-form-select
        v-model="filters.publico"
        :options="publicoOptions"
        class="form-control"
        :disabled="isInstrutor"
        @change="filtrar"
      />

      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('titulo')">
              Título {{ arrow('titulo') }}
            </button>
          </th>

          <th>
            <button class="btn btn-link p-0" @click="changeSort('descricao')">
              Descrição {{ arrow('descricao') }}
            </button>
          </th>

          <!-- ✅ Público sem sort (agora é array) -->
          <th>Público</th>

          <th class="text-left">Material</th>
          <th class="text-center">Ações</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="m in materiais" :key="m.id">
          <td>{{ m.titulo }}</td>
          <td>{{ m.descricao }}</td>
          <td>{{ formatPublico(m.publico) }}</td>

          <!-- Coluna Material com link de download -->
          <td class="text-left">
            <template v-if="m.arquivo_nome && m.caminho">
              <a
                class="file-link"
                :href="apiUrl + m.caminho"
                download
                target="_blank"
                rel="noopener"
                @click="markAndDownload(m)"
              >
                {{ m.arquivo_nome }}
              </a>
              <i
                v-if="m.visualizado === false"
                class="bi bi-patch-exclamation-fill text-info ms-1"
                title="Novo"
              ></i>
            </template>
            <span v-else>–</span>
          </td>

          <td class="text-end">
            <router-link
              v-if="hasPermission('alterar_materiais')"
              :to="`/ajuda/materiais/${m.id}`"
            >
              <i class="bi bi-pencil-fill text-warning me-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_materiais')"
              class="bi bi-trash-fill text-danger me-2"
              title="Excluir"
              @click="excluir(m.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>

      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const { fetchPermissions, hasPermission } = usePermissions()

const apiUrl = process.env.VUE_APP_API_URL

// perfil (pra replicar regra do Instrutor)
const { appContext } = getCurrentInstance()
const perfil = appContext.config.globalProperties.$globalState.userProfile
const isInstrutor = computed(() => !!perfil?.includes('Instrutor'))

const publicoOptions = [
  { value: '', text: 'Todos os públicos' },
  { value: 'Instrutor', text: 'Instrutor' },
  { value: 'Apoio', text: 'Apoio' },
  { value: 'Diretoria', text: 'Diretoria' }
]

function formatPublico(p) {
  if (Array.isArray(p)) return p.join(', ')
  if (typeof p === 'string') return p
  return '–'
}

const {
  items: materiais,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort: changeSort,
  arrow
} = useSortableTable(
  `/ajuda/materiais`,
  { key: 'criado_em', order: 'desc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])
const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow = computed(() => Math.min(page.value * limit.value, totalItems.value))

function filtrar() {
  page.value = 1
  load()
}

function limparFiltros() {
  filters.value.titulo = ''
  filters.value.descricao = ''

  // ✅ instrutor não pode "limpar" o público
  if (!isInstrutor.value) {
    filters.value.publico = ''
  } else {
    filters.value.publico = 'Instrutor'
  }

  page.value = 1
  load()
}

function changeLimit() {
  page.value = 1
  load()
}

async function excluir(id) {
  if (confirm('Tem certeza que deseja excluir este material?')) {
    await axios.delete(`/ajuda/materiais/${id}`)
    load()
  }
}

function markAndDownload(m) {
  if (m.visualizado === false) {
    axios
      .post('/ajuda/visualizacoes', {
        tipo: 'material',
        item_id: m.id
      })
      .catch(err => console.error(err))
      .finally(() => {
        m.visualizado = true
      })
  }
  // download segue via href
}

watch(page, load)
watch(limit, () => {
  page.value = 1
  load()
})

onMounted(async () => {
  await fetchPermissions()

  filters.value.titulo = ''
  filters.value.descricao = ''

  // ✅ regra Instrutor
  if (isInstrutor.value) {
    filters.value.publico = 'Instrutor'
  } else {
    filters.value.publico = ''
  }

  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
.table-hover tr:hover {
  background-color: var(--bs-light);
}

.file-link {
  text-decoration: none;
}
.file-link:hover {
  text-decoration: underline;
}
</style>
